import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";
import axios from "axios";
import baseURL from "@/http";
import { getValidToken } from "@/pages/public/Login/checkTokenValidity";

export interface UploadItem {
  id: string;
  file: File;
  title: string;
  description: string;
  progress: number;
  /**
   * "processing" is the phase between the last byte leaving the browser and the
   * server answering. It used to be invisible: `onUploadProgress` reaches 100%
   * when the request body has been *sent*, and everything the server does after
   * that -- storing the file, creating the record, queueing conversion --
   * happened while the widget still said "Uploading 100%". A 512 MB video sat
   * there for over an hour with no way to tell whether it was working or dead.
   */
  status: "pending" | "uploading" | "processing" | "completed" | "error";
  error?: string;
  /** When the server-side phase began, used to show how long it has been. */
  processingSince?: number;

  course_id?: string;
  chapter_id?: string;
  heading_id?: string;
  institute_id?: string | number;
  meeting_id?: string | number;

  video_type: string;
  video_id?: string;
  sourceId?: string; // storage path
  endpoint: string;
  thumbnail?: File;
  duration?: number;

  /**
   * A plain file upload rather than a video: the file goes in `field`, with
   * `extra` beside it, and the endpoint answers with the `{ success, message,
   * data }` envelope. `onResponse` gets `data`; `onFailed` gets the reason.
   */
  form?: { field: string; extra?: Record<string, string> };
  onResponse?: (data: any) => void;
  onFailed?: (message: string) => void;

  // Callback invoked when upload completes successfully
  onComplete?: (data: {
    fileRecordId: string;
    sourceId?: string;
    title: string;
    description: string;
    thumbnail?: File;
  }) => void;
}

interface GlobalUploadContextType {
  uploadQueue: UploadItem[];
  addVideosToQueue: (
    videos: {
      file: File;
      title: string;
      description: string;
      thumbnail?: File;
      duration?: number;
      id?: string;
    }[],
    metadata: any,
    onComplete?: (data: {
      fileRecordId: string;
      sourceId?: string;
      title: string;
      description: string;
      thumbnail?: File;
    }) => void
  ) => void;
  /** Plain files, one request each, shown in the same tray as videos. */
  addFilesToQueue: (files: File[], options: FileUploadOptions) => void;
  removeUpload: (id: string) => void;
  retryUpload: (id: string) => void;
}

export interface FileUploadOptions {
  /** Path after the API base, e.g. "/interlearn/fmea/parts/12/documents/". */
  endpoint: string;
  /** The form field the file goes in. Default "file". */
  field?: string;
  extra?: Record<string, string>;
  /** The tray's second line, e.g. "Source documents · Cylinder block". */
  label?: string;
  onResponse?: (data: any) => void;
  onFailed?: (message: string) => void;
}

const GlobalUploadContext =
  createContext<GlobalUploadContextType | undefined>(undefined);

/** The tray when the page sits inside the provider, else undefined (callers fall back). */
export const useOptionalUpload = () => useContext(GlobalUploadContext);

export const useUpload = () => {
  const context = useContext(GlobalUploadContext);
  if (!context) {
    throw new Error(
      "useUpload must be used within a GlobalUploadProvider"
    );
  }
  return context;
};

export const GlobalUploadProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [uploadQueue, setUploadQueue] = useState<UploadItem[]>([]);

  const processingQueue = useRef<Set<string>>(new Set());

  // NEW → for canceling active uploads
  const abortControllers = useRef<
    Map<string, AbortController>
  >(new Map());

  const addVideosToQueue = useCallback(
    (
      videos: {
        file: File;
        title: string;
        description: string;
        thumbnail?: File;
        duration?: number;
        id?: string;
      }[],
      metadata: any,
      onComplete?: (data: {
        fileRecordId: string;
        sourceId?: string;
        title: string;
        description: string;
        thumbnail?: File;
      }) => void
    ) => {
      const newItems: UploadItem[] = videos.map((v) => ({
        id: v.id || crypto.randomUUID(),
        file: v.file,
        title: v.title,
        description: v.description,
        thumbnail: v.thumbnail,
        duration: v.duration,
        progress: 0,
        status: "pending",

        course_id: metadata.course_id,
        chapter_id: metadata.chapter_id,
        heading_id: metadata.heading_id,
        institute_id: metadata.institute_id,
        meeting_id: metadata.meeting_id,

        video_type: metadata.video_type || "local",
        endpoint: metadata.endpoint || "/interlearn/video/",
        
        // Attach the completion callback
        onComplete,
      }));

      setUploadQueue((prev) => [...prev, ...newItems]);
    },
    []
  );

  const addFilesToQueue = useCallback((files: File[], options: FileUploadOptions) => {
    const items: UploadItem[] = files.map((file) => ({
      id: crypto.randomUUID(),
      file,
      title: file.name,
      description: options.label ?? "",
      progress: 0,
      status: "pending",
      video_type: "file",
      endpoint: options.endpoint,
      form: { field: options.field ?? "file", extra: options.extra },
      onResponse: options.onResponse,
      onFailed: options.onFailed,
    }));
    setUploadQueue((prev) => [...prev, ...items]);
  }, []);

  // UPDATED → cancel upload + remove queue item
  const removeUpload = useCallback((id: string) => {
    abortControllers.current.get(id)?.abort();
    abortControllers.current.delete(id);

    processingQueue.current.delete(id);

    setUploadQueue((prev) =>
      prev.filter((item) => item.id !== id)
    );
  }, []);

  const updateProgress = useCallback(
    (id: string, progress: number) => {
      setUploadQueue((prev) =>
        prev.map((item) =>
          item.id === id
            ? {
              ...item,
              progress,
              status: "uploading",
            }
            : item
        )
      );
    },
    []
  );

  const markProcessing = useCallback((id: string) => {
    setUploadQueue((prev) =>
      prev.map((item) =>
        item.id === id && item.status === "uploading"
          ? {
            ...item,
            progress: 100,
            status: "processing",
            processingSince: Date.now(),
          }
          : item
      )
    );
  }, []);

  const markCompleted = useCallback(
    (id: string, video_id: string, sourceId?: string) => {
      setUploadQueue((prev) => {
        const updated: UploadItem[] = prev.map((item) => {
          if (item.id !== id) return item;

          // Trigger completion callback before removing
          if (item.onComplete) {
            item.onComplete({
              fileRecordId: video_id,
              sourceId,
              title: item.title,
              description: item.description,
              thumbnail: item.thumbnail,
            });
          }

          const completedItem: UploadItem = {
            ...item,
            progress: 100,
            status: "completed",
            video_id,
            sourceId,
          };
          return completedItem;
        });

        return updated;
      });

      processingQueue.current.delete(id);
      abortControllers.current.delete(id);
      setTimeout(() => {
        setUploadQueue((prev) =>
          prev.filter((item) => item.id !== id)
        );
      }, 3000);
    },
    []
  );

  const markError = useCallback(
    (id: string, error: string) => {
      setUploadQueue((prev) =>
        prev.map((item) =>
          item.id === id
            ? {
              ...item,
              status: "error",
              error,
            }
            : item
        )
      );

      processingQueue.current.delete(id);
      abortControllers.current.delete(id);
    },
    []
  );

  const processUpload = useCallback(
    async (item: UploadItem) => {
      if (processingQueue.current.has(item.id)) return;

      processingQueue.current.add(item.id);

      // create abort controller
      const controller = new AbortController();
      abortControllers.current.set(item.id, controller);

      // immediately mark uploading
      setUploadQueue((prev) =>
        prev.map((u) =>
          u.id === item.id
            ? {
              ...u,
              status: "uploading",
              progress: 1,
            }
            : u
        )
      );

      const formData = new FormData();
      if (item.form) {
        formData.append(item.form.field, item.file);
        Object.entries(item.form.extra ?? {}).forEach(([k, v]) => formData.append(k, v));
      } else {
      formData.append("VideoTitle", item.title);
      formData.append("description", item.description);
      formData.append("video_type", item.video_type);

      if (item.course_id)
        formData.append("course_id", item.course_id);

      if (item.chapter_id)
        formData.append("chapter_id", item.chapter_id);

      if (item.heading_id)
        formData.append("heading_id", item.heading_id);

      if (item.institute_id) {
        formData.append(
          "institute_id",
          String(item.institute_id)
        );
      }

      if (item.meeting_id) {
        formData.append(
          "meeting_id",
          String(item.meeting_id)
        );
      }

      formData.append("file", item.file);

      if (item.thumbnail) {
        formData.append("thumbnail", item.thumbnail);
      }

      if (item.duration !== undefined && item.duration !== null && !isNaN(Number(item.duration))) {
        formData.append("duration", String(item.duration));
        formData.append("VideoDuration", String(item.duration));
      }

      formData.append("custom_name[]", item.title);
      }

      try {
        const token = await getValidToken();

        if (!token) {
          throw new Error("No authentication token");
        }

        const response = await axios.post(
          `${baseURL}${item.endpoint}`,
          formData,
          {
            signal: controller.signal, // NEW
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
            // No axios timeout on purpose: a multi-GB class recording can
            // legitimately take a long time, and cutting it off mid-transfer
            // would waste the whole upload. The stall detector below is what
            // distinguishes "still working" from "hung" -- a timeout cannot,
            // because it cannot see whether bytes are still moving.
            onUploadProgress: (progressEvent) => {
              if (progressEvent.total) {
                const progress = Math.round(
                  (progressEvent.loaded * 100) /
                  progressEvent.total
                );

                if (progress >= 100) {
                  // Body fully sent. Everything from here is the server
                  // working, so say so rather than leaving a 100% bar that
                  // looks identical to a finished upload.
                  markProcessing(item.id);
                } else {
                  updateProgress(item.id, progress);
                }
              }
            },
          }
        );

        if (item.form) {
          // A plain file: the `{ success, message, data }` envelope.
          const body = response.data;
          if (body && typeof body === "object" && "success" in body && !body.success) {
            throw new Error(body.message || "Upload failed");
          }
          item.onResponse?.(body?.data ?? body);
          markCompleted(item.id, "");
          return;
        }

        if (
          response.data?.status === "Ok" ||
          response.data?.status === "ok"
        ) {
          const uploaded = response.data?.data?.[0];

          markCompleted(
            item.id,
            uploaded?.db_id || uploaded?.id,
            uploaded?.file_path
          );

          if (item.heading_id || item.chapter_id || item.course_id) {
            window.dispatchEvent(
              new CustomEvent("video-upload-completed", {
                detail: {
                  heading_id: item.heading_id || item.chapter_id,
                  chapter_id: item.chapter_id,
                  course_id: item.course_id,
                  video_links: response.data?.video_links || response.data?.data,
                  videoTableId: response.data?.id,
                },
              })
            );
          }

          if (item.institute_id) {
            window.dispatchEvent(
              new CustomEvent(
                "institute-file-upload-completed",
                {
                  detail: {
                    institute_id: item.institute_id,
                  },
                }
              )
            );
          }

          if (item.meeting_id) {
            // single file uploaded event
            window.dispatchEvent(
              new CustomEvent(
                "meeting-file-upload-completed",
                {
                  detail: {
                    meeting_id: item.meeting_id,
                    file_id: uploaded?.db_id || uploaded?.id,
                    sourceId: uploaded?.file_path,
                    title: item.title,
                    provider: item.video_type,
                  },
                }
              )
            );

            // NEW → check whether all meeting uploads finished
            setUploadQueue((prev) => {
              const remainingUploads = prev.filter(
                (u) =>
                  String(u.meeting_id) === String(item.meeting_id) &&
                  u.id !== item.id &&
                  (
                    u.status === "pending" ||
                    u.status === "uploading" ||
                    u.status === "processing"
                  )
              );

              if (remainingUploads.length === 0) {
                window.dispatchEvent(
                  new CustomEvent(
                    "meeting-all-files-uploaded",
                    {
                      detail: {
                        meeting_id: item.meeting_id,
                      },
                    }
                  )
                );
              }

              return prev;
            });
          }
        } else {
          throw new Error(
            response.data?.message || "Upload failed"
          );
        }
      } catch (err: any) {
        // canceled upload → silently remove
        if (
          err?.name === "CanceledError" ||
          err?.code === "ERR_CANCELED"
        ) {
          processingQueue.current.delete(item.id);
          abortControllers.current.delete(item.id);

          setUploadQueue((prev) =>
            prev.filter((u) => u.id !== item.id)
          );

          return;
        }
        const reason = err?.response?.data?.message || err.message || "Network error";
        item.onFailed?.(reason);
        markError(
          item.id,
          reason
        );
      }
    },
    [updateProgress, markProcessing, markCompleted, markError]
  );

  useEffect(() => {
    const pending = uploadQueue.find(
      (item) => item.status === "pending"
    );

    if (pending) {
      processUpload(pending);
    }
  }, [uploadQueue, processUpload]);

  const retryUpload = useCallback((id: string) => {
    setUploadQueue((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
            ...item,
            status: "pending",
            progress: 0,
            error: undefined,
          }
          : item
      )
    );
  }, []);

  return (
    <GlobalUploadContext.Provider
      value={{
        uploadQueue,
        addVideosToQueue,
        addFilesToQueue,
        removeUpload,
        retryUpload,
      }}
    >
      {children}
    </GlobalUploadContext.Provider>
  );
};